import type { Database } from 'node-sqlite3-wasm';
import { createSqliteDatabaseAccess } from '../access/index.js';
import { isCliSqliteLockError } from '../sqlite-errors.js';
import { ensureCodeNavCacheSqliteParentDir, getCodeNavCacheSqlitePath } from './code-nav-cache-paths.js';
import { migrateCodeNavCacheSqlite } from './migrate-code-nav-cache-sqlite.js';
import { reportCodeNavCacheLockContention } from './code-nav-cache-log.js';
import {
  resetCodeNavCacheSqliteWorkLock,
  withCodeNavCacheSqliteWorkLock,
} from './code-nav-cache-sqlite-work-lock.js';
import {
  decrementCodeNavCacheAccessDepth,
  getCodeNavCacheAccessDepth,
  incrementCodeNavCacheAccessDepth,
  resetCodeNavCacheAccessDepth,
  waitForOuterCodeNavCacheAccessIfNeededAsync,
  waitForOuterCodeNavCacheAccessIfNeededSync,
} from './code-nav-cache-access-depth.js';

export const codeNavCacheSqlite = createSqliteDatabaseAccess({
  getPath: getCodeNavCacheSqlitePath,
  ensureParentDir: ensureCodeNavCacheSqliteParentDir,
  migrate: migrateCodeNavCacheSqlite,
  onLockRetry: ({ attempt, maxAttempts, final }) => {
    if (attempt === 1 || final) {
      reportCodeNavCacheLockContention('SQLite open', { attempt, maxAttempts, final });
    }
  },
});

export async function withCodeNavCacheSqliteWorkLockWrapped<T>(
  fn: (db: Database) => T | Promise<T>,
): Promise<T> {
  return withCodeNavCacheSqliteWorkLock(async () => {
    const nested = getCodeNavCacheAccessDepth() > 0;
    await waitForOuterCodeNavCacheAccessIfNeededAsync(nested);
    incrementCodeNavCacheAccessDepth();
    try {
      return await codeNavCacheSqlite.withAsync(fn);
    } finally {
      decrementCodeNavCacheAccessDepth();
    }
  });
}

export async function withCodeNavCacheSqliteWorkLockProbeWrapped<T>(
  fn: (db: Database) => T | Promise<T>,
): Promise<T | undefined> {
  try {
    return await withCodeNavCacheSqliteWorkLock(async () => {
      const nested = getCodeNavCacheAccessDepth() > 0;
      await waitForOuterCodeNavCacheAccessIfNeededAsync(nested);
      incrementCodeNavCacheAccessDepth();
      try {
        return await codeNavCacheSqlite.withAsyncProbe(fn);
      } finally {
        decrementCodeNavCacheAccessDepth();
      }
    });
  } catch (e) {
    if (isCliSqliteLockError(e)) return undefined;
    throw e;
  }
}

export function withCodeNavCacheSqliteSyncWrapped<T>(fn: (db: Database) => T): T {
  const nested = getCodeNavCacheAccessDepth() > 0;
  waitForOuterCodeNavCacheAccessIfNeededSync(nested);
  incrementCodeNavCacheAccessDepth();
  try {
    return codeNavCacheSqlite.withSync(fn);
  } finally {
    decrementCodeNavCacheAccessDepth();
  }
}

export function closeAllCodeNavCacheSqliteConnections(): void {
  codeNavCacheSqlite.closeAllConnections();
  resetCodeNavCacheSqliteWorkLock();
  resetCodeNavCacheAccessDepth();
}
